import { useEffect, useMemo, useRef, useState } from 'react';

const COLS = 3;
const ROWS = 3;

// Random order with no piece already home and never solved (the PHP "expert" difficulty).
function shuffled() {
  const n = COLS * ROWS;
  for (;;) {
    const a = Array.from({ length: n }, (_, i) => i);
    for (let i = n - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    if (a.every((v, i) => v !== i)) return a;
  }
}

/**
 * Swap-style jigsaw (the puzzle plugin inside stage3.php): drag a piece onto another to
 * swap them. Pieces are greyscale until they sit in the right slot and then lock.
 * Fires onFinished once every piece is home.
 */
export default function ManorPuzzle({ image, aspect, onFinished }) {
  const [order, setOrder] = useState(shuffled); // order[slot] = piece id
  const [drag, setDrag] = useState(null); // { slot, x, y }
  const [over, setOver] = useState(null);
  const start = useRef(null);
  const done = useRef(false);

  const solved = useMemo(() => order.every((p, i) => p === i), [order]);

  useEffect(() => {
    if (solved && !done.current) {
      done.current = true;
      onFinished?.();
    }
  }, [solved, onFinished]);

  // The dragged piece sits on top of the pointer, so skip it to find the slot underneath.
  const slotAt = (x, y, skip) => {
    const el = document.elementsFromPoint(x, y).find((n) => n.dataset?.slot !== undefined && Number(n.dataset.slot) !== skip);
    return el ? Number(el.dataset.slot) : null;
  };

  const down = (slot) => (e) => {
    if (order[slot] === slot || solved) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    start.current = { x: e.clientX, y: e.clientY };
    setDrag({ slot, x: 0, y: 0 });
  };
  const move = (e) => {
    const from = start.current;
    if (!from) return;
    // Read the start point now: the state updater below runs later, after pointer-up may have cleared it.
    const x = e.clientX - from.x;
    const y = e.clientY - from.y;
    setDrag((d) => d && { ...d, x, y });
    const t = slotAt(e.clientX, e.clientY, drag?.slot);
    setOver(t !== null && order[t] !== t ? t : null);
  };
  const up = (e) => {
    if (!start.current) return;
    start.current = null;
    const from = drag?.slot;
    const to = slotAt(e.clientX, e.clientY, from);
    setDrag(null);
    setOver(null);
    if (from === undefined || to === null || to === from || order[to] === to) return;
    setOrder((o) => {
      const n = [...o];
      [n[from], n[to]] = [n[to], n[from]];
      return n;
    });
  };

  return (
    <div className="manor-puzzle" style={{ aspectRatio: String(aspect) }}>
      <div className="manor-puzzle-grid">
        {order.map((piece, slot) => {
          const home = piece === slot;
          const col = piece % COLS;
          const row = Math.floor(piece / COLS);
          const dragging = drag?.slot === slot;
          return (
            <div
              key={slot}
              data-slot={slot}
              className={`manor-piece${home ? ' home' : ''}${over === slot ? ' over' : ''}${dragging ? ' dragging' : ''}`}
              style={{
                backgroundImage: `url(${image})`,
                backgroundSize: `${COLS * 100}% ${ROWS * 100}%`,
                backgroundPosition: `${(col / (COLS - 1)) * 100}% ${(row / (ROWS - 1)) * 100}%`,
                transform: dragging ? `translate(${drag.x}px, ${drag.y}px)` : undefined,
              }}
              onPointerDown={down(slot)}
              onPointerMove={move}
              onPointerUp={up}
              onPointerCancel={up}
            />
          );
        })}
      </div>
      <img className={`manor-puzzle-full${solved ? ' show' : ''}`} src={image} alt="" />
    </div>
  );
}
